import {MapItem} from '../components/map/map-item/map-item';
import {LuminizerLayerMarkerDataRef, LuminizerMapMarker} from '../components/map/map-core.interface';
import {TranslateService} from '../services/translate/translate.service';

export interface WFSInfoWindowCallbacks {
    // Any interaction inside the popup (pagination/filter/buttons).
    onInteract?: () => void;
    // Selecting the feature (clicking the popup body or its id).
    onSelect: (mapItem: MapItem) => void;
    // Opening street view for the feature.
    onOpenStreetview: (mapItem: MapItem) => void;
    // Starting a route to the feature.
    onStartRoute: (routeMarker: LuminizerMapMarker) => void;
}

// Builds the DOM content for a WFS feature hover popup.
export default class MapWFSInfoWindowBuilder {
    private static readonly PAGE_SIZE: number = 5; // Default attributes shown per page
    private static readonly FILTER_MIN_FIELDS: number = 6; // Only show the filter box beyond this many attributes

    constructor(private ts: TranslateService) {
    }

    // Entry point: returns the full popup content element for the given hovered WFS feature.
    public build(markerData: LuminizerLayerMarkerDataRef, showRouteButton: boolean, callbacks: WFSInfoWindowCallbacks): HTMLElement {
        const dataLayerLabels = (markerData.dataLayerLabels || []).filter((label) => !label.name.startsWith('__'));
        const preferredObjectId = dataLayerLabels.find((_x) => /^objectid$/i.test(_x.name));
        const displayObjectId = preferredObjectId ? String(preferredObjectId.value) : String(markerData.id);

        const wfsMapItem: MapItem = <any>{
            id: String(markerData.id),
            label: displayObjectId,
            lat: markerData.lat,
            lng: markerData.lng,
            dataLayer: true,
            dataLayerLabels: dataLayerLabels,
        };

        const wfsRouteMarker: LuminizerMapMarker = <any>{
            position: new google.maps.LatLng(markerData.lat, markerData.lng),
            dataRef: wfsMapItem,
        };

        const wrapperDiv: HTMLElement = document.createElement('div');
        wrapperDiv.className = 'map-infowindow-wfs';

        wrapperDiv.appendChild(this.buildHeader(wfsMapItem, wfsRouteMarker, markerData, displayObjectId, showRouteButton, callbacks));

        if (dataLayerLabels.length > 0) {
            wrapperDiv.appendChild(this.buildAttributeTable(dataLayerLabels, callbacks));
        }

        return wrapperDiv;
    }

    // Header row: streetview + route action buttons and the clickable feature id.
    private buildHeader(
        wfsMapItem: MapItem,
        wfsRouteMarker: LuminizerMapMarker,
        markerData: LuminizerLayerMarkerDataRef,
        displayObjectId: string,
        showRouteButton: boolean,
        callbacks: WFSInfoWindowCallbacks,
    ): HTMLElement {
        const markInteracting = () => {
            if (callbacks.onInteract) {
                callbacks.onInteract();
            }
        };

        const headerDiv: HTMLElement = document.createElement('div');
        headerDiv.className = 'map-infowindow-container';
        headerDiv.onclick = () => {
            markInteracting();
            callbacks.onSelect(wfsMapItem);
        };

        // Street view button
        headerDiv.appendChild(this.createActionButton('visibility', this.ts.translate('streetview.view'), (e) => {
            e.stopPropagation();
            markInteracting();
            callbacks.onOpenStreetview(wfsMapItem);
        }, 'open-streetview-btn'));

        // Route button
        if (showRouteButton) {
            headerDiv.appendChild(this.createActionButton('directions_car', this.ts.translate('route.view'), (e) => {
                e.stopPropagation();
                markInteracting();
                callbacks.onStartRoute(wfsRouteMarker);
            }));
        }

        // Clickable feature id (selects the item)
        const baseObjectContainerDiv: HTMLElement = document.createElement('div');
        baseObjectContainerDiv.className = 'map-infowindow-baseobject-container';

        const codeDiv: HTMLElement = document.createElement('div');
        codeDiv.className = 'map-infowindow-code-button';
        codeDiv.setAttribute('data-value', String(markerData.id));
        codeDiv.title = this.ts.translate('Selecteer item:') + ' ' + displayObjectId;
        const pinIcon: HTMLElement = document.createElement('i');
        pinIcon.className = 'material-icons map-streetview-icon';
        pinIcon.textContent = 'place';
        codeDiv.appendChild(pinIcon);
        codeDiv.appendChild(document.createTextNode(displayObjectId));
        codeDiv.onclick = (e) => {
            e.stopPropagation();
            markInteracting();
            callbacks.onSelect(wfsMapItem);
        };
        baseObjectContainerDiv.appendChild(codeDiv);
        headerDiv.appendChild(baseObjectContainerDiv);

        return headerDiv;
    }

    // Small factory for the round icon buttons in the WFS popup header.
    private createActionButton(iconName: string, title: string, onClick: (e: MouseEvent) => void, dataTest?: string): HTMLElement {
        const buttonWrapper: HTMLElement = document.createElement('div');
        const button: HTMLElement = document.createElement('div');
        button.className = 'map-infowindow-streetview-button';
        button.title = title;
        if (dataTest) {
            button.setAttribute('data-test', dataTest);
        }
        const icon: HTMLElement = document.createElement('i');
        icon.className = 'material-icons map-streetview-icon';
        icon.textContent = iconName;
        button.appendChild(icon);
        buttonWrapper.appendChild(button);
        buttonWrapper.onclick = onClick;
        return buttonWrapper;
    }

    // Generic, schema-agnostic attribute table with live filtering and pagination.
    private buildAttributeTable(dataLayerLabels: {name: string, value: any}[], callbacks: WFSInfoWindowCallbacks): HTMLElement {
        const pageSize = MapWFSInfoWindowBuilder.PAGE_SIZE;
        const tableContainer: HTMLElement = document.createElement('div');
        tableContainer.className = 'map-infowindow-wfs-table';

        // State (kept local to this popup instance via closure)
        let filterText = '';
        let currentPage = 0;

        // Filter box, only shown when there are enough fields to warrant it
        let searchInput: HTMLInputElement | null = null;
        if (dataLayerLabels.length > MapWFSInfoWindowBuilder.FILTER_MIN_FIELDS) {
            const searchWrapper: HTMLElement = document.createElement('div');
            searchWrapper.className = 'map-infowindow-wfs-search';

            const searchIcon: HTMLElement = document.createElement('i');
            searchIcon.className = 'material-icons map-infowindow-wfs-search-icon';
            searchIcon.textContent = 'search';
            searchWrapper.appendChild(searchIcon);

            const inputEl = document.createElement('input');
            searchInput = inputEl;
            searchInput.type = 'text';
            searchInput.placeholder = this.ts.translate('map.wfs.filterfields');
            searchInput.oninput = () => {
                if (callbacks.onInteract) {
                    callbacks.onInteract();
                }
                filterText = inputEl.value.trim().toLowerCase();
                currentPage = 0;
                render();
            };
            // Keep clicks inside the input from bubbling to the popup (which would select the item)
            searchInput.onclick = (e) => {
                e.stopPropagation();
                if (callbacks.onInteract) {
                    callbacks.onInteract();
                }
            };
            searchWrapper.appendChild(searchInput);
            tableContainer.appendChild(searchWrapper);
        }

        const rowsDiv: HTMLElement = document.createElement('div');
        rowsDiv.className = 'map-infowindow-wfs-rows';
        tableContainer.appendChild(rowsDiv);

        const pagerDiv: HTMLElement = document.createElement('div');
        pagerDiv.className = 'map-infowindow-wfs-pager';

        const countSpan: HTMLElement = document.createElement('span');
        countSpan.className = 'map-infowindow-wfs-count';

        const navSpan: HTMLElement = document.createElement('span');
        navSpan.className = 'map-infowindow-wfs-nav';

        const prevButton: HTMLElement = document.createElement('span');
        prevButton.className = 'map-infowindow-wfs-pg-btn';
        prevButton.textContent = '‹';

        const pageIndicator: HTMLElement = document.createElement('span');
        pageIndicator.className = 'map-infowindow-wfs-pg-ind';

        const nextButton: HTMLElement = document.createElement('span');
        nextButton.className = 'map-infowindow-wfs-pg-btn';
        nextButton.textContent = '›';

        navSpan.appendChild(prevButton);
        navSpan.appendChild(pageIndicator);
        navSpan.appendChild(nextButton);
        pagerDiv.appendChild(countSpan);
        pagerDiv.appendChild(navSpan);
        tableContainer.appendChild(pagerDiv);

        const getFilteredLabels = (): {name: string, value: any}[] => {
            if (!filterText) {
                return dataLayerLabels;
            }
            return dataLayerLabels.filter((label) =>
                label.name.toLowerCase().indexOf(filterText) !== -1 ||
                String(label.value ?? '').toLowerCase().indexOf(filterText) !== -1
            );
        };

        const render = () => {
            const filtered = getFilteredLabels();
            const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
            currentPage = Math.min(currentPage, totalPages - 1);

            const startIndex = currentPage * pageSize;
            const pageLabels = filtered.slice(startIndex, startIndex + pageSize);

            // Rebuild rows
            rowsDiv.innerHTML = '';
            if (pageLabels.length === 0) {
                const emptyRow: HTMLElement = document.createElement('div');
                emptyRow.className = 'map-infowindow-wfs-empty';
                emptyRow.textContent = this.ts.translate('map.wfs.nomatches');
                rowsDiv.appendChild(emptyRow);

                // Keep popup height stable while typing a filter that yields no results,
                // so InfoWindow reflow does not trigger an accidental mouseleave close.
                for (let i = 1; i < pageSize; i++) {
                    const placeholderRow: HTMLElement = document.createElement('div');
                    placeholderRow.className = 'map-infowindow-wfs-row map-infowindow-wfs-row-placeholder';
                    placeholderRow.innerHTML = '&nbsp;';
                    rowsDiv.appendChild(placeholderRow);
                }
            } else {
                pageLabels.forEach((label) => {
                    const rowDiv: HTMLElement = document.createElement('div');
                    rowDiv.className = 'map-infowindow-wfs-row';

                    const keySpan: HTMLElement = document.createElement('span');
                    keySpan.className = 'map-infowindow-wfs-key';
                    keySpan.textContent = label.name;

                    const valueSpan: HTMLElement = document.createElement('span');
                    valueSpan.className = 'map-infowindow-wfs-value';
                    valueSpan.textContent = String(label.value ?? '');

                    rowDiv.appendChild(keySpan);
                    rowDiv.appendChild(valueSpan);
                    rowsDiv.appendChild(rowDiv);
                });

                // Pad partial pages with hidden placeholder rows so the popup keeps a
                // constant height across all filter and pagination states. Without this,
                // shrinking to fewer rows can trigger InfoWindow reflow/reposition and
                // cause a spurious mouseleave close.
                if (pageLabels.length < pageSize) {
                    for (let i = pageLabels.length; i < pageSize; i++) {
                        const placeholderRow: HTMLElement = document.createElement('div');
                        placeholderRow.className = 'map-infowindow-wfs-row map-infowindow-wfs-row-placeholder';
                        placeholderRow.innerHTML = '&nbsp;';
                        rowsDiv.appendChild(placeholderRow);
                    }
                }
            }

            // Update pager text
            if (filterText) {
                countSpan.textContent = this.ts.translate('map.wfs.matchcount').replace('{count}', String(filtered.length));
            } else if (filtered.length === 0) {
                countSpan.textContent = '';
            } else {
                const endIndex = Math.min(startIndex + pageSize, filtered.length);
                countSpan.textContent = `${startIndex + 1}\u2013${endIndex} / ${filtered.length}`;
            }
            pageIndicator.textContent = `${currentPage + 1} / ${totalPages}`;

            // Update nav button state
            const atFirst = currentPage <= 0;
            const atLast = currentPage >= totalPages - 1;
            prevButton.classList.toggle('disabled', atFirst);
            nextButton.classList.toggle('disabled', atLast);
            navSpan.style.display = totalPages > 1 ? 'flex' : 'none';
        };

        prevButton.onclick = (e) => {
            e.stopPropagation();
            if (callbacks.onInteract) {
                callbacks.onInteract();
            }
            if (currentPage > 0) {
                currentPage--;
                render();
            }
        };
        nextButton.onclick = (e) => {
            e.stopPropagation();
            if (callbacks.onInteract) {
                callbacks.onInteract();
            }
            const totalPages = Math.max(1, Math.ceil(getFilteredLabels().length / pageSize));
            if (currentPage < totalPages - 1) {
                currentPage++;
                render();
            }
        };

        render();

        return tableContainer;
    }
}
